// src/pages/Products.jsx
// grabItems  → { data: [{id, product_name, manufacture, sku, min_price, max_price, tax, active}] }
// addItem    → add new product
// updateItem → update existing product
import React, { useState, useEffect, useCallback } from 'react';
import { Products as API } from '../services/api';
import { useAuth } from '../context/AuthContext';

const EMPTY = { id: 0, product_name: '', manufacture: '', sku: '', tax: 18, min_price: '', max_price: '' };

export default function Products() {
  const { user }  = useAuth();
  const [list, setList]     = useState([]);
  const [busy, setBusy]     = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal]   = useState(false);
  const [form, setForm]     = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');

  const load = useCallback(() => {
    setBusy(true);
    API.list().then(setList).catch(console.error).finally(() => setBusy(false));
  }, []);
  useEffect(load, [load]);

  const openAdd  = ()  => { setForm({ ...EMPTY, corp_id: user.corp_id }); setModal(true); setErr(''); };
  const openEdit = (p) => { setForm({ ...p, corp_id: user.corp_id });    setModal(true); setErr(''); };

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    if (!form.product_name) return 'Product name required';
    if (!form.sku)          return 'SKU required';
    if (!form.min_price)    return 'Min price required';
    if (!form.max_price)    return 'Max price required';
    if (parseFloat(form.min_price) > parseFloat(form.max_price)) return 'Min price cannot exceed max price';
    return '';
  };

  const save = async () => {
    const e = validate();
    if (e) { setErr(e); return; }
    setSaving(true); setErr('');
    try {
      if (form.id) {
        await API.update(form);
      } else {
        await API.add(form);
      }
      setModal(false);
      load();
    } catch (ex) { setErr(ex.message); }
    finally { setSaving(false); }
  };

  const filtered = list.filter(p =>
    !search ||
    (p.product_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.sku          || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.manufacture  || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title">📦 Products</div>
        <input className="search-bar" placeholder="Search name, SKU, brand…"
          value={search} onChange={e => setSearch(e.target.value)} />
        <button className="btn btn-primary" onClick={openAdd}>+ Add Product</button>
      </div>

      {busy ? <div className="spinner-wrap"><div className="spinner" /></div> :
        filtered.length === 0 ? <div className="empty">No products found</div> :
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>SKU</th><th>Product Name</th><th>Brand</th>
                <th>Min Price</th><th>Max Price</th><th>Tax %</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id}>
                  <td><code>{p.sku}</code></td>
                  <td className="fw-bold">{p.product_name}</td>
                  <td style={{ color: 'var(--muted)' }}>{p.manufacture || '—'}</td>
                  <td>₹{parseFloat(p.min_price).toLocaleString()}</td>
                  <td>₹{parseFloat(p.max_price).toLocaleString()}</td>
                  <td>{p.tax}%</td>
                  <td>
                    <button className="btn btn-sm btn-ghost" onClick={() => openEdit(p)}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{form.id ? 'Edit Product' : 'Add Product'}</span>
              <button className="btn-icon" onClick={() => setModal(false)}>✕</button>
            </div>
            {err && <div className="alert alert-error">{err}</div>}
            <label style={{ marginBottom: '1rem' }}>
              Product Name *
              <input value={form.product_name} onChange={set('product_name')} autoFocus />
            </label>
            <label style={{ marginBottom: '1rem' }}>
              Brand / Manufacturer
              <input value={form.manufacture} onChange={set('manufacture')} />
            </label>
            <label style={{ marginBottom: '1rem' }}>
              SKU * <span style={{ color: 'var(--faint)', fontSize: '.75rem' }}>(must be unique)</span>
              <input value={form.sku} onChange={set('sku')} />
            </label>
            <div className="form-row" style={{ marginBottom: '1rem' }}>
              <label>Min Price (₹) *<input type="number" step="0.01" value={form.min_price} onChange={set('min_price')} /></label>
              <label>Max Price (₹) *<input type="number" step="0.01" value={form.max_price} onChange={set('max_price')} /></label>
            </div>
            <label style={{ marginBottom: '1.2rem' }}>
              Tax % (GST)
              <input type="number" step="0.01" value={form.tax} onChange={set('tax')} />
            </label>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : form.id ? 'Update Product' : 'Add Product'}
              </button>
              <button className="btn btn-ghost" onClick={() => setModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
